import array from 'common/helpers/primitives/array'
import sentry from 'common/helpers/analytics/sentry'


interface IImageSizeProps {
  src: string
  w?: number
  h?: number
  x2?: boolean
  x3?: boolean
  srcSet?: string
  quality?: number
  productId?: number
}

interface IModifiedImageSizeProps {
  src: string,
  srcSet?: string
  w?: number
  h?: number
}

const getModifiedSrc = (src: string, params: object): string => {
  const keys = Object.keys(params).filter((key) => params[key])

  if (keys.length) {
    const prefix = /\?/.test(src) ? '&' : '?'
    const query  = keys.map((key) => `${key}=${params[key]}`).join('&')

    return `${src}${prefix}${query}`
  }

  return src
}

const getFilledSrcAndSrcSetWithQuality = (props): IModifiedImageSizeProps => {
  const { src, srcSet, quality, ...rest } = props

  if (!quality) {
    return props
  }

  const prefix = /\?/.test(src) ? '&' : '?'
  const qlty = `${prefix}qlty=${quality}`

  const modifiedSrc = `${src}${qlty}`

  // name.jpg?params x2, name-2.jpg?params x3 -> name.jpg?params&qlty=60 x2, name-2.jpg?params&qlty=60 x3
  const modifiedSrcSet = typeof srcSet === 'string' ? srcSet.replace(/(\.(png|jpe?g|webp)[^ ,]*)/g, `$1${qlty}`) : srcSet

  return {
    ...rest,
    src: modifiedSrc,
    srcSet: modifiedSrcSet,
  }
}

const getLambdaSizes = (props: IImageSizeProps): IModifiedImageSizeProps => {
  const { src, w, h, quality } = props

  const key   = Boolean(w) ? 'w' : 'h'
  const value = w || h

  return { src: getModifiedSrc(src, { [key]: value, qlty: quality }) }
}

const getLambdaSrcSet = (props: IImageSizeProps): IModifiedImageSizeProps => {
  const { src, srcSet, w, h, x3, quality } = props

  const maxSize = x3 ? 3 : 2
  const key     = Boolean(w) ? 'w' : 'h'
  const value   = w || h

  let modifiedSrcSet

  if (srcSet) {
    modifiedSrcSet = srcSet.replace(/\s[0-9]x/g, '')
      .split(', ')
      .map((source, index) => {
        const size = index + 1
        const modifiedSrc = getModifiedSrc(source, { [key]: value * size, qlty: quality })

        return `${modifiedSrc}${index ? ` ${size}x` : ''}`
      })
      .join(', ')
  }
  else {
    modifiedSrcSet = array.range(1, maxSize)
      .map((size, index) => {
        const modifiedSrc = getModifiedSrc(src, { [key]: value * size, qlty: quality })

        // image.png?w=200
        // image.png?w=400 2x
        // image.png?w=600 3x
        return `${modifiedSrc}${index ? ` ${size}x` : ''}`
      })
      .join(', ')
  }

  return {
    ...getLambdaSizes(props),
    srcSet: modifiedSrcSet,
  }
}

const getSrcSet = (props: IImageSizeProps): IModifiedImageSizeProps => {
  // TODO we have wrong Image usage - import 2x,3x + props 2x,3x - added on 09.01.2020 by sonatskiy
  if (props.src && props.srcSet) {
    return getFilledSrcAndSrcSetWithQuality(props)
  }

  const { src, x2, x3, quality } = props

  const sizes = [ x2, x3 ]
  const modifiedSrc = getModifiedSrc(src, { qlty: quality })

  let srcSet = modifiedSrc

  sizes.forEach((size, index) => {
    if (size) {
      // image.png -> image@2x.png
      const url = modifiedSrc.replace(/(\.(png|jpe?g))/, `@${index + 2}x$1`)

      srcSet += `, ${url} ${index + 2}x`
    }
  })

  return { src: modifiedSrc, srcSet }
}

const modifyImageSizeProps = (props: IImageSizeProps): IModifiedImageSizeProps => {
  const { src, srcSet, w, h, x2, x3, quality, ...rest } = props

  if (!src) {
    sentry.captureException('Missed "src" value', {
      extra: {
        props,
        placement: 'ModifyImageSizeProps',
      },
    })

    return props
  }

  const isLambdaParamsExist = w || h || quality
  const isMultiplierExist   = x2 || x3
  let result: IModifiedImageSizeProps

  if (isLambdaParamsExist && (isMultiplierExist || srcSet)) {
    result = getLambdaSrcSet(props)
  }
  else if (isMultiplierExist) {
    // TODO it's a ductape for images returned by backend - added on 09.01.2020 by sonatskiy
    // backend returns: image.jpg and on S3 we have image@2x.jpg, image@3x.jpg
    // so we should create srcSet by modifying initial src
    // looks it one place only: site/client/components/ui/Tags/Tag/Tag
    result = getSrcSet(props)
  }
  else if (isLambdaParamsExist) {
    result = getLambdaSizes(props)
  }
  else {
    // nothing to modify so pass as is
    return props
  }

  return { ...rest, w, h, ...result }
}


export default modifyImageSizeProps
